"use client";

import { useEffect, useRef } from "react";
import { cn } from "@/lib/utils";

interface ParticleTextProps {
  /** The word to render (e.g. a city name). */
  text: string;
  className?: string;
}

/**
 * Renders a word as a cloud of dots that fly in and assemble into the
 * letterforms — a bespoke, on-brand headline flourish (moss with brass accents
 * on parchment). Purely decorative: the canvas is aria-hidden, so the page must
 * provide the real text elsewhere (e.g. an sr-only span in the heading).
 *
 * Respects prefers-reduced-motion (dots appear in place, no animation), is
 * responsive (re-samples on resize), and stops animating once assembled to keep
 * it light on the CPU.
 */
export function ParticleText({ text, className }: ParticleTextProps) {
  const wrapRef = useRef<HTMLSpanElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !canvas) return;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return;

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const DPR = Math.min(window.devicePixelRatio || 1, 2);

    const css = getComputedStyle(document.documentElement);
    const moss = css.getPropertyValue("--color-primary").trim() || "#1e3a2b";
    const brass = css.getPropertyValue("--color-secondary").trim() || "#8a5616";

    interface P {
      x: number;
      y: number;
      tx: number;
      ty: number;
      c: string;
    }

    let particles: P[] = [];
    let raf = 0;
    let startedAt = 0;
    let Wd = 0;
    let Hd = 0;
    let lastW = 0;
    let resizeTimer = 0;

    const ASSEMBLE_MS = 1300;
    const STAGGER_MS = 520;

    function build() {
      if (!wrap || !canvas || !ctx) return;
      const w = Math.max(1, wrap.clientWidth);
      // Fit the font to the available width.
      const fontSize = Math.min(Math.max(w / (text.length * 0.62), 44), 156);
      const h = Math.round(fontSize * 1.22);

      Wd = Math.round(w * DPR);
      Hd = Math.round(h * DPR);
      canvas.width = Wd;
      canvas.height = Hd;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;

      const fpx = fontSize * DPR;
      ctx.clearRect(0, 0, Wd, Hd);
      ctx.fillStyle = "#000";
      ctx.font = `800 ${fpx}px ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(text, Wd / 2, Hd / 2);

      const data = ctx.getImageData(0, 0, Wd, Hd).data;
      const gap = Math.max(4, Math.round(fpx / 22));

      const targets: { x: number; y: number }[] = [];
      for (let y = 0; y < Hd; y += gap) {
        for (let x = 0; x < Wd; x += gap) {
          if (data[(y * Wd + x) * 4 + 3] > 130) targets.push({ x, y });
        }
      }
      ctx.clearRect(0, 0, Wd, Hd);

      // Start each dot in a tight halo around its target so the word stays
      // legible the whole way in (rather than a full-canvas blob).
      const spread = Math.min(Wd, Hd) * 0.55;
      particles = targets.map((t, i) => {
        const ang = Math.random() * Math.PI * 2;
        const rad = 24 + Math.random() * spread;
        return {
          tx: t.x,
          ty: t.y,
          x: reduced ? t.x : t.x + Math.cos(ang) * rad,
          y: reduced ? t.y : t.y + Math.sin(ang) * rad,
          // A scattering of brass dots among the moss.
          c: i % 7 === 0 ? brass : moss,
        };
      });
      lastW = w;
      startedAt = performance.now();
    }

    function draw(now: number) {
      if (!ctx) return;
      ctx.clearRect(0, 0, Wd, Hd);
      const r = DPR * 1.5;
      const elapsed = now - startedAt;
      let moving = false;

      for (const p of particles) {
        let alpha = 1;
        if (!reduced) {
          const delay = (p.tx / Math.max(1, Wd)) * STAGGER_MS;
          const k = Math.min(1, Math.max(0, (elapsed - delay) / ASSEMBLE_MS));
          const ease = 1 - Math.pow(1 - k, 3);
          p.x += (p.tx - p.x) * (0.06 + 0.16 * ease);
          p.y += (p.ty - p.y) * (0.06 + 0.16 * ease);
          alpha = 0.25 + 0.75 * ease;
          if (k < 1 || Math.abs(p.tx - p.x) > 0.4 || Math.abs(p.ty - p.y) > 0.4) {
            moving = true;
          }
        }
        ctx.globalAlpha = alpha;
        ctx.beginPath();
        ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
        ctx.fillStyle = p.c;
        ctx.fill();
      }
      ctx.globalAlpha = 1;

      if (!reduced && moving) {
        raf = requestAnimationFrame(draw);
      }
    }

    function run() {
      cancelAnimationFrame(raf);
      build();
      raf = requestAnimationFrame(draw);
    }

    run();

    const ro = new ResizeObserver(() => {
      const w = wrap.clientWidth;
      if (Math.abs(w - lastW) < 8) return;
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(run, 160);
    });
    ro.observe(wrap);

    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(resizeTimer);
      ro.disconnect();
    };
  }, [text]);

  return (
    <span ref={wrapRef} aria-hidden="true" className={cn("block w-full", className)}>
      <canvas ref={canvasRef} className="block" />
    </span>
  );
}
